import { Epic, ofType } from 'redux-observable'
import { filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { ENDPOINTS } from './constants'
import { AccountingEvent, events } from './state'
import { multiUserOperators } from '../../multiUser/state'
import { accountingApiTransformers } from '.'
import { AnyAction } from 'redux'
import { Observable } from 'rxjs'
import { $TSFixMe } from 'types/ts-migrate'
import { Base64PdfResponse } from './types/Base64PdfResponse'
import { JsonResponse } from './types/JsonResponse'
import { Base64CsvResponse } from './types/Base64CsvResponse'
import { BillingAgencySettingsResponse } from './types/BillingAgencySettingsResponse'
import { EpicDependencies, RootState } from 'src/state/types'

export const apiFetchBankStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.bankStatementJson_request.toString())),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_BANK_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) => events.bankStatementJson_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchBankStatementJson'),
  )

export const apiFetchBankStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.bankStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_BANK_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) => events.bankStatementPdf_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchBankStatementPdf'),
  )

export const apiFetchBankStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.bankStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_BANK_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) => events.bankStatementCsv_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchBankStatementCsv'),
  )

// export const apiFetchCustomerStatements = (
//   action$: Observable<AnyAction>,
//   state$: $TSFixMe,
//   { get, catchRestError }: $TSFixMe,
// ) =>
//   action$.pipe(
//     ofType(events.customerStatements_request),
//     mergeMap((action: AnyAction) =>
//       get(ENDPOINTS.FETCH_CUSTOMER_STATEMENTS, state$, action.payload).pipe(
//         pluck('response'),
//         map(events.customerStatements_success),
//         catchRestError(action),
//       ),
//     ),
//     tag('accounting/epics/apiFetchCustomerStatements'),
//   )

export const apiFetchCustomerStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.customerStatementJson_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CUSTOMER_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) =>
          events.customerStatementJson_success({
            response,
            customerId: action.payload.customerId,
            accountId: action.payload.accountId,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCustomerStatementJson'),
  )

export const apiFetchCustomerStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.customerStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CUSTOMER_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) =>
          events.customerStatementPdf_success({
            response,
            customerId: action.payload.customerId,
            accountId: action.payload.accountId,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCustomerStatementPdf'),
  )

export const apiFetchCustomerStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.customerStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CUSTOMER_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) =>
          events.customerStatementCsv_success({
            response,
            customerId: action.payload.customerId,
            accountId: action.payload.accountId,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCustomerStatementCsv'),
  )

// Tenant Statement PDF (CU-869dmfep1): fetched in place of the Account
// Statement PDF on a lease's Tenant wallet. Resolves into
// customerStatementPdf_success keyed by the same customer/account pair, so the
// ViewStatement sidebar's download effect fires exactly as it does for the
// Account Statement. Only the path/query params go to the endpoint — the
// customer/account ids ride along solely for the success key.
export const apiFetchTenantStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tenantStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.TENANT_STATEMENT_PDF, state$, {
        portfolioId: action.payload.portfolioId,
        start_date: action.payload.start_date,
        end_date: action.payload.end_date,
      }).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) =>
          events.customerStatementPdf_success({
            response,
            customerId: action.payload.customerId,
            accountId: action.payload.accountId,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchTenantStatementPdf'),
  )

// Tenant Statement CSV: same substitution as the PDF above, resolving into
// customerStatementCsv_success under the customer/account key.
export const apiFetchTenantStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.tenantStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.TENANT_STATEMENT_CSV, state$, {
        portfolioId: action.payload.portfolioId,
        start_date: action.payload.start_date,
        end_date: action.payload.end_date,
      }).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) =>
          events.customerStatementCsv_success({
            response,
            customerId: action.payload.customerId,
            accountId: action.payload.accountId,
          }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchTenantStatementCsv'),
  )

export const apiFetchConsolidatedStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.consolidatedStatementJson_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CONSOLIDATED_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) =>
          events.consolidatedStatementJson_success({ response, partyId: action.payload.partyId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchConsolidatedStatementJson'),
  )

export const apiFetchConsolidatedStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.consolidatedStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CONSOLIDATED_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) =>
          events.consolidatedStatementPdf_success({ response, partyId: action.payload.partyId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchConsolidatedStatementPdf'),
  )

export const apiFetchConsolidatedStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.consolidatedStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CONSOLIDATED_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) =>
          events.consolidatedStatementCsv_success({ response, partyId: action.payload.partyId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchConsolidatedStatementCsv'),
  )

export const apiFetchPortfolioStatement: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.portfolioStatement_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.LEASE_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) =>
          events.portfolioStatement_success({ response, portfolioId: action.payload.portfolioId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchPortfolioStatement'),
  )

export const apiFetchPortfolioStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.portfolioStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.LEASE_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) =>
          events.portfolioStatementPdf_success({ response, portfolioId: action.payload.portfolioId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchPortfolioStatementPdf'),
  )

export const apiFetchPortfolioStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.portfolioStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.LEASE_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) =>
          events.portfolioStatementCsv_success({ response, portfolioId: action.payload.portfolioId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchPortfolioStatementCsv'),
  )

export const apiFetchDepositStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositStatementJson_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) =>
          events.depositStatementJson_success({ response, accountId: action.payload.accountId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositStatementJson'),
  )

export const apiFetchDepositStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) =>
          events.depositStatementPdf_success({ response, accountId: action.payload.accountId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositStatementPdf'),
  )

export const apiFetchDepositStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) =>
          events.depositStatementCsv_success({ response, accountId: action.payload.accountId }),
        ),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositStatementCsv'),
  )

export const apiFetchDepositBankStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositBankStatementJson_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_BANK_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map(events.depositBankStatementJson_success),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositBankStatementJson'),
  )

export const apiFetchDepositBankStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositBankStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_BANK_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map(events.depositBankStatementPdf_success),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositBankStatementPdf'),
  )

export const apiFetchDepositBankStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.depositBankStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.DEPOSIT_BANK_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map(events.depositBankStatementCsv_success),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchDepositBankStatementCsv'),
  )

export const apiFetchCommissionStatementJson: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.commissionStatementJson_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.COMMISSION_STATEMENT_JSON, state$, action.payload).pipe(
        pluck('response'),
        map((response: JsonResponse) => events.commissionStatementJson_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCommissionStatementJson'),
  )

export const apiFetchCommissionStatementPdf: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.commissionStatementPdf_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.COMMISSION_STATEMENT_PDF, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64PdfResponse) => events.commissionStatementPdf_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCommissionStatementPdf'),
  )

export const apiFetchCommissionStatementCsv: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.commissionStatementCsv_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.COMMISSION_STATEMENT_CSV, state$, action.payload).pipe(
        pluck('response'),
        map((response: Base64CsvResponse) => events.commissionStatementCsv_success({ response })),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchCommissionStatementCsv'),
  )

export const apiGetStats: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.accountingStats_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.GET_STATS_ACCOUNTING, state$, action.payload).pipe(
        pluck('response'),
        map(res => events.accountingStats_success(accountingApiTransformers.transformStats(res))),
        catchRestError(action),
      ),
    ),
    tag('portfolio/epics/apiGetStats'),
  )

export const apiFetchBillingAgencySettings: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { get, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.billingAgencySettings_request.toString())),
    mergeMap(action =>
      get(ENDPOINTS.BILLING_AGENCY_SETTINGS, state$, action.payload).pipe(
        pluck('response'),
        map((response: BillingAgencySettingsResponse) => events.billingAgencySettings_success(response)),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiFetchBillingAgencySettings'),
  )

// Number goes on the query string, body stays empty. Already a JS number by
// here — the panel parses and range-checks before dispatching.
export const apiUpdateBillingInvoiceNumber: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.updateBillingInvoiceNumber_request.toString())),
    mergeMap(action =>
      post(ENDPOINTS.UPDATE_BILLING_INVOICE_NUMBER, state$, {}, { number: action.payload.number }).pipe(
        map(() => events.updateBillingInvoiceNumber_success()),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiUpdateBillingInvoiceNumber'),
  )

export const apiSetBillingAgencyDiscounts: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.setBillingAgencyDiscounts_request.toString())),
    mergeMap(action =>
      post(
        ENDPOINTS.BILLING_AGENCY_DISCOUNTS,
        state$,
        {
          percentProcessingFeeDiscount: action.payload.percentProcessingFeeDiscount,
          percentTransactionFeeDiscount: action.payload.percentTransactionFeeDiscount,
          notes: action.payload.notes,
        },
        { agencyId: action.payload.agencyId },
      ).pipe(
        map(() => events.setBillingAgencyDiscounts_success()),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiSetBillingAgencyDiscounts'),
  )

export const apiRemoveBillingAgencySettings: Epic<AccountingEvent, AccountingEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  { post, catchRestError },
) =>
  action$.pipe(
    filter(isOfType(events.removeBillingAgencySettings_request.toString())),
    mergeMap(action =>
      post(ENDPOINTS.REMOVE_BILLING_AGENCY, state$, {}, { agencyId: action.payload.agencyId }).pipe(
        map(() => events.removeBillingAgencySettings_success()),
        catchRestError(action),
      ),
    ),
    tag('accounting/epics/apiRemoveBillingAgencySettings'),
  )

// Re-read after a write — the counter is global, so reading it back is the
// only trustworthy confirmation of what the table now holds.
export const refetchBillingAgencySettings: Epic<
  AccountingEvent,
  AccountingEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(
      isOfType([
        events.updateBillingInvoiceNumber_success.toString(),
        events.setBillingAgencyDiscounts_success.toString(),
        events.removeBillingAgencySettings_success.toString(),
      ]),
    ),
    map(() => events.billingAgencySettings_request()),
    tag('accounting/epics/refetchBillingAgencySettings'),
  )
